import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { profitabilityRunPath } from "@/profitability/routes";
import type { EnrichedProfitabilityPeriod } from "@/profitability/types";
import { PeriodHistorySheet } from "./period-history-sheet";
import { PeriodPicker } from "./period-picker";
import { periodResultId } from "./run-select";
import type { TemplatePeriod } from "./use-template-period";

/**
 * The period at the top of Review's findings column, the way the invoices
 * inbox titles its list. The date itself opens the period menu. Upload,
 * share, and the agent menu stay on the other pages.
 */
export const ReviewPeriod = ({
  businessId,
  reportId,
  reportName,
  period,
  onPeriodChange,
}: {
  businessId: string;
  reportId: string;
  reportName: string;
  period: TemplatePeriod;
  onPeriodChange: (runId: string | null) => void;
}) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState(false);
  const { overview, run, label, periodChanging, requestedId } = period;
  const data = overview.data;
  const openRun = (runId: string, row: EnrichedProfitabilityPeriod) => {
    if (row.status === "completed" && row.workbook_available) {
      setHistory(false);
      onPeriodChange(runId);
      return;
    }
    void navigate(profitabilityRunPath(runId));
  };

  return (
    <>
      <Stack className="min-w-0 flex-1">
        {(overview.isPending && !run) || (periodChanging && !label) ? (
          <Skeleton className="h-7 w-48" />
        ) : run ? (
          <h1 className="min-w-0">
            <PeriodPicker
              variant="button"
              triggerLabel={label ?? reportName}
              periods={data?.periods ?? []}
              current={
                periodChanging && requestedId
                  ? requestedId
                  : periodResultId(data?.runs, run.id)
              }
              onChange={onPeriodChange}
              onAllPeriods={() => setHistory(true)}
            />
          </h1>
        ) : (
          <Text
            render={<h1 />}
            variant="title"
            tabular
            truncate
            className="w-full min-w-0"
          >
            {reportName}
          </Text>
        )}
      </Stack>
      <PeriodHistorySheet
        open={history}
        onOpenChange={setHistory}
        businessId={businessId}
        reportId={reportId}
        onOpenRun={openRun}
      />
    </>
  );
};
